import { useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { type ObjectType, type WorkflowStatus } from "store/appData";
import { type ImageAnnotation } from "../api";

const FALLBACK_BOX_COLOR = "#1976d2";

// Coordinates are normalised to [0, 1] (x/y = top-left corner). `annotations`
// is expected to be pre-filtered to the object types worth showing, with the
// type to display first.
export default function AnnotationOverlay({
  annotations,
  objectTypes,
  workflowStatuses,
  width,
  height,
}: {
  annotations: ImageAnnotation[];
  objectTypes: Record<number, ObjectType>;
  workflowStatuses: Record<number, WorkflowStatus>;
  width: number;
  height: number;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  // Only the smallest box under the pointer shows its label, so overlapping
  // boxes don't drown each other out.
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        ref={overlayRef}
        data-testid="datasetImagePanel.annotationOverlay"
        onMouseMove={(event) => {
          const el = overlayRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) return;
          const px = (event.clientX - rect.left) / rect.width;
          const py = (event.clientY - rect.top) / rect.height;
          let best: ImageAnnotation | null = null;
          for (const a of annotations) {
            const inside =
              px >= a.x &&
              px <= a.x + a.width &&
              py >= a.y &&
              py <= a.y + a.height;
            if (!inside) continue;
            if (!best || a.width * a.height < best.width * best.height) best = a;
          }
          setHoveredId(best ? best.id : null);
        }}
        onMouseLeave={() => setHoveredId(null)}
        sx={{ position: "relative", width, height, pointerEvents: "auto" }}
      >
        {annotations.map((annotation) => {
          const annotationType = annotation.types[0];
          const objectType =
            annotationType?.object_type_id != null
              ? objectTypes[annotationType.object_type_id]
              : undefined;
          const objectTypeName = objectType?.en_name;
          const color = objectType?.color || FALLBACK_BOX_COLOR;
          const hovered = annotation.id === hoveredId;
          // Second tooltip row: the box's workflow status, resolved from its
          // id. Boxes without a workflow status simply omit the row.
          const workflowStatusName =
            annotationType?.workflow_status != null
              ? workflowStatuses[annotationType.workflow_status]?.name
              : undefined;
          return (
            <Tooltip
              key={annotation.id}
              title={
                objectTypeName ? (
                  <>
                    <Box>{objectTypeName}</Box>
                    {workflowStatusName && (
                      <Box
                        data-testid={`datasetImagePanel.annotationWorkflowStatus.${annotation.id}`}
                        sx={{ fontSize: "0.85em", opacity: 0.7 }}
                      >
                        {workflowStatusName}
                      </Box>
                    )}
                  </>
                ) : (
                  ""
                )
              }
              open={Boolean(hovered && objectTypeName)}
              placement="top"
              arrow
              disableHoverListener
              disableFocusListener
              disableTouchListener
            >
              <Box
                data-testid={`datasetImagePanel.annotation.${annotation.id}`}
                sx={{
                  position: "absolute",
                  left: `${100 * annotation.x}%`,
                  top: `${100 * annotation.y}%`,
                  width: `${100 * annotation.width}%`,
                  height: `${100 * annotation.height}%`,
                  border: `2px ${hovered ? "solid" : "dashed"} ${color}`,
                  boxSizing: "border-box",
                  pointerEvents: "none",
                  zIndex: hovered ? 1 : undefined,
                  transform: annotation.rotation
                    ? `rotate(${annotation.rotation}deg)`
                    : undefined,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
